/**
 * Valutazione posizionale per l'AI
 * Punteggio positivo = vantaggio bianco, negativo = vantaggio nero
 */

import type { BoardState, Piece } from '@draughts/shared';
import { Color, PieceType, GameVariant, VARIANT_CONFIGS, VARIANT_SPECS, hasStandardRules } from '@draughts/shared';
import { squareToPosition } from '../board/square-utils.js';
import { getAllLegalMoves } from '../moves/capture-moves.js';

// Valori dei pezzi
const PIECE_VALUES = {
  [PieceType.MAN]: 100,
  [PieceType.KING]: 300, // Le dame volanti sono molto potenti (meno per Italian)
};

/**
 * Valore di una dama NON volante (si muove di una casella per volta).
 *
 * Si chiedeva alla variante per NOME (`isItalian || isEnglish`), non alla regola:
 * Triestina e Filippina — entrambe attive, entrambe senza dama volante —
 * pagavano la loro dama 300 invece di 250. Ora la domanda è
 * `VARIANT_SPECS[...].rules.flyingKings`, che è la fonte, e il nome della
 * costante dice quale regola rappresenta.
 */
const NON_FLYING_KING_VALUE = 250;

// Bonus per avanzamento (pedine più avanti valgono di più)
const ADVANCEMENT_BONUS = 5; // punti per riga di avanzamento

// Bonus per controllo del centro
const CENTER_BONUS = 10;

/**
 * Caselle centrali per l'Italiana 8x8 (`darkSquareBottomRight=true`).
 * Righe 3-6, colonne 3-6 → caselle 10, 11, 14, 15, 18, 19, 22, 23.
 *
 * L'UNICA eccezione tarata a mano che sopravvive: quattro righe invece delle due
 * di mezzo, cioè una fascia deliberatamente più larga. Tutte le altre si
 * calcolano (vedi `caselleCentrali`), e per International, Inglese e Brasiliana
 * il calcolo riproduce ESATTAMENTE i set che erano scritti a mano — quindi per
 * loro il comportamento non cambia di un punto.
 */
const CENTER_SQUARES_ITALIAN_8x8 = new Set([10, 11, 14, 15, 18, 19, 22, 23]);

/** Per le board all-squares il set non viene consultato: quel ramo usa riga/colonna. */
const EMPTY_CENTER: ReadonlySet<number> = new Set<number>();

/** Memoria dei set calcolati, per non rifare il giro a ogni valutazione. */
const centerCache = new Map<string, ReadonlySet<number>>();

/**
 * Le caselle «centrali» di una damiera dark-only, calcolate dalla geometria:
 * le DUE RIGHE di mezzo, colonne 3..N-2 (cioè escluse le due esterne per lato).
 *
 * Prima esisteva un solo ripiego — il set tarato per il 10x10 — usato da
 * QUINDICI varianti attive con geometrie diverse, e il risultato era un bonus
 * «centro» che cadeva altrove:
 *
 *   • undici varianti 8x8 (Russa, Spagnola, Pool, Ceca, Thai, Portoghese,
 *     Filippina, Tedesca, Giamaicana, Singalese) ricevevano le caselle 22-29,
 *     che su otto righe stanno alle RIGHE 6-7-8 — la metà campo avversaria;
 *   • Canadese e Malese (12x12) le vedevano alle righe 4-5 su dodici;
 *   • Sudafricana (14x14) alle righe 4-5 su quattordici.
 *
 * Il rapporto d'audit aveva contato due varianti; sono quindici.
 *
 * Esportata perché è l'unica fonte della fascia centrale, e un test la interroga
 * direttamente: dedurla dal punteggio finale non si può — `evaluatePosition`
 * somma anche mobilità e avanzamento, e il bonus centro non è più isolabile.
 */
export function caselleCentrali(size: number, darkSquareBottomRight: boolean): ReadonlySet<number> {
  const chiave = `${size}:${darkSquareBottomRight}`;
  const memo = centerCache.get(chiave);
  if (memo) return memo;

  const rigaMin = size / 2;
  const rigaMax = size / 2 + 1;
  const set = new Set<number>();
  for (let square = 1; square <= (size * size) / 2; square++) {
    const pos = squareToPosition(square, size, darkSquareBottomRight, false);
    if (pos.row >= rigaMin && pos.row <= rigaMax && pos.col >= 3 && pos.col <= size - 2) {
      set.add(square);
    }
  }
  centerCache.set(chiave, set);
  return set;
}

// Penalità per caselle sui bordi (meno mobili)
const EDGE_PENALTY = -5;

// Bonus per mobilità (mosse legali disponibili)
const MOBILITY_BONUS = 3;

// ── Soglie per l'accettazione della patta da parte dell'AI ──────────────
// L'AI rifiuta se è in vantaggio di più di ~mezza pedina (una pedina = 100).
const DRAW_ACCEPT_EVAL_THRESHOLD = 50;
// Considera "finale semplificato" una posizione con pochi pezzi sulla scacchiera.
const DRAW_ACCEPT_MAX_PIECES = 8;
// Considera "posizione stagnante" un lungo tratto senza progressi.
const DRAW_ACCEPT_NO_PROGRESS_PLIES = 20;

/**
 * Calcola il punteggio di una posizione
 * @param board - Stato della scacchiera
 * @returns Punteggio (positivo = vantaggio bianco, negativo = vantaggio nero)
 *
 * In antidama (`ruleMode: 'antidraughts'`) il punteggio viene NEGATO in blocco: l'obiettivo
 * è rovesciato, quindi si rovesciano insieme materiale, avanzamento, centro, bordi e mobilità.
 * Negarne solo una parte creerebbe termini in conflitto proprio nelle posizioni di zugzwang,
 * che sono il motivo per cui si gioca l'antidama.
 *
 * È un'approssimazione euristica, non teoria misère: qui "avanzare" diventa "resta indietro"
 * e "promuovere" diventa "non promuovere", mentre nell'antidama reale una dama sola è spesso
 * utile perché costringe l'avversario a catturare. La parte esatta del gioco in modalità non
 * standard sta altrove — nel rilevamento dei finali di `determineGameStatus`, che il minimax
 * ora propaga fino alle foglie.
 */
export function evaluatePosition(board: BoardState): number {
  const score = evaluateStandard(board);
  return (board.ruleMode ?? 'normal') === 'antidraughts' ? -score : score;
}

/** Valutazione con la convenzione standard: positivo = vantaggio bianco. */
function evaluateStandard(board: BoardState): number {
  let score = 0;
  const variantConfig = VARIANT_CONFIGS[board.variant];
  const boardSize = variantConfig.size;
  const darkSquareBottomRight = variantConfig.darkSquareBottomRight;
  // All-squares (Turkish/Armenian): pezzi numerati 1..N² e non solo sulle case scure.
  // Senza questo flag squareToPosition tratterebbe la casella come dark-only e lancerebbe
  // "Invalid square" per ogni casella > N²/2.
  const allSquaresPlayable = variantConfig.playableSquares === boardSize * boardSize;
  /*
   * Le due domande che prima si facevano per NOME di variante, e che ora si
   * fanno alla regola e alla geometria:
   *  - quanto vale una dama → `flyingKings` della matrice;
   *  - quali sono le caselle centrali → calcolate da size/darkSquareBottomRight.
   * L'Italiana resta l'unica eccezione tarata a mano sul centro (fascia più larga).
   * Per le board all-squares il set non serve: quel ramo valuta la fascia dalla
   * posizione, e `caselleCentrali` (dark-only) lancerebbe su una casella > N²/2.
   */
  const flyingKings = VARIANT_SPECS[board.variant].rules.flyingKings;
  const centerSquares = allSquaresPlayable
    ? EMPTY_CENTER
    : board.variant === GameVariant.ITALIAN
      ? CENTER_SQUARES_ITALIAN_8x8
      : caselleCentrali(boardSize, darkSquareBottomRight);

  for (const piece of board.pieces) {
    const pieceScore = evaluatePiece(piece, flyingKings, centerSquares, boardSize, darkSquareBottomRight, allSquaresPlayable);

    if (piece.color === Color.WHITE) {
      score += pieceScore;
    } else {
      score -= pieceScore;
    }
  }

  // Mobilità: chi ha il turno ha un vantaggio se ha più mosse
  const currentMoves = getAllLegalMoves(board);
  const mobilityScore = currentMoves.length * MOBILITY_BONUS;
  if (board.currentTurn === Color.WHITE) {
    score += mobilityScore;
  } else {
    score -= mobilityScore;
  }

  return score;
}

/**
 * Valuta un singolo pezzo
 */
function evaluatePiece(
  piece: Piece,
  flyingKings: boolean,
  centerSquares: ReadonlySet<number>,
  boardSize: number,
  darkSquareBottomRight: boolean,
  allSquaresPlayable: boolean,
): number {
  // Valore base del pezzo
  let score: number;
  if (piece.type === PieceType.KING) {
    // Una dama che non vola vale meno: si muove di una casella per volta.
    // La domanda va alla REGOLA, non al nome della variante — Triestina e
    // Filippina, entrambe attive e senza dama volante, pagavano 300 invece di 250.
    score = flyingKings ? PIECE_VALUES[PieceType.KING] : NON_FLYING_KING_VALUE;
  } else {
    score = PIECE_VALUES[PieceType.MAN];
  }

  const pos = squareToPosition(piece.square, boardSize, darkSquareBottomRight, allSquaresPlayable);

  // Bonus avanzamento per le pedine
  if (piece.type === PieceType.MAN) {
    if (piece.color === Color.WHITE) {
      // Bianco avanza verso riga 1
      score += (boardSize - pos.row) * ADVANCEMENT_BONUS;
    } else {
      // Nero avanza verso riga boardSize
      score += (pos.row - 1) * ADVANCEMENT_BONUS;
    }
  }

  // Bonus centro. Per le board all-squares (Turkish/Armenian) le caselle 1..N² non
  // corrispondono agli indici dark-only hardcoded qui sotto: valuta la fascia centrale dalla
  // posizione (riga/colonna). Le varianti dark-only mantengono i loro set tarati.
  if (allSquaresPlayable) {
    const low = 3;
    const high = boardSize - 2;
    if (pos.row >= low && pos.row <= high && pos.col >= low && pos.col <= high) {
      score += CENTER_BONUS;
    }
  } else if (centerSquares.has(piece.square)) {
    score += CENTER_BONUS;
  }

  // Penalità bordi (colonne 1 e ultima)
  if (pos.col === 1 || pos.col === boardSize) {
    score += EDGE_PENALTY;
  }

  return score;
}

/**
 * Valutazione veloce solo materiale (per sorting mosse)
 */
export function evaluateMaterial(board: BoardState): number {
  let score = 0;
  // Stessa correzione di `evaluatePiece`: la regola, non il nome.
  const flyingKings = VARIANT_SPECS[board.variant].rules.flyingKings;

  for (const piece of board.pieces) {
    let value: number;
    if (piece.type === PieceType.KING && !flyingKings) {
      value = NON_FLYING_KING_VALUE;
    } else {
      value = PIECE_VALUES[piece.type];
    }
    if (piece.color === Color.WHITE) {
      score += value;
    } else {
      score -= value;
    }
  }

  return score;
}

/**
 * Decide se l'AI dovrebbe accettare un'offerta di patta.
 * Accetta solo quando NON è in vantaggio e la posizione è genuinamente patta
 * (finale semplificato o lungo tratto senza progressi).
 * @param board - Stato della scacchiera
 * @param aiColor - Colore giocato dall'AI
 */
export function shouldAcceptDraw(board: BoardState, aiColor: Color): boolean {
  // evaluatePosition è positivo per il Bianco: normalizza dal punto di vista dell'AI.
  const evalForAI = evaluatePosition(board) * (aiColor === Color.WHITE ? 1 : -1);
  // Se l'AI è in vantaggio di più di ~mezza pedina, continua a giocare.
  if (evalForAI > DRAW_ACCEPT_EVAL_THRESHOLD) return false;
  const totalPieces = board.pieces.length;
  const noProgress = (board.halfMoveClock ?? 0) >= DRAW_ACCEPT_NO_PROGRESS_PLIES;
  // "Pochi pezzi ⇒ posizione patta" è un'intuizione da REGOLE normali. Dove le regole
  // cambiano dice il contrario del vero: in antidama restare con pochi pezzi vuol dire
  // essere vicini a VINCERE, e in breakthrough una pedina a due traverse dalla dama decide
  // la partita. Il predicato, e non un confronto con 'normal': alla cieca le regole SONO
  // quelle normali — cambia solo cosa si vede — e un confronto sul nome della modalità
  // cambierebbe in silenzio il gioco dell'AI in una partita bendata.
  const simplifiedEndgame = hasStandardRules(board.ruleMode) && totalPieces <= DRAW_ACCEPT_MAX_PIECES;
  return simplifiedEndgame || noProgress;
}
